import React from 'react';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import { CssBaseline, Box } from '@mui/material';
import { AuthProvider } from './context/AuthContext';
import { TaskProvider } from './context/TaskContext';
import TaskSync from './components/TaskSync';
import AppRoutes from './components/Router';
import ErrorBoundary from './components/ErrorBoundary';

// Create Material-UI theme - AI Dashboard Inspired Theme
const theme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: '#000000', // Black primary color
      light: '#333333', // Dark gray for hover
      dark: '#000000',
      contrastText: '#ffffff',
    },
    secondary: {
      main: '#000000', // Black secondary color
      light: '#333333',
      dark: '#000000',
      contrastText: '#ffffff',
    },
    background: {
      default: '#f8fafc', // AI Dashboard background
      paper: '#ffffff', // White paper
    },
    text: {
      primary: '#1565C0', // Match navigation header active color
      secondary: '#6b7280',
    },
    success: {
      main: '#059669',
      light: '#10b981',
      dark: '#047857',
    },
    warning: {
      main: '#d97706',
      light: '#f59e0b',
      dark: '#b45309',
    },
    error: {
      main: '#dc2626',
      light: '#ef4444',
      dark: '#b91c1c',
    },
    info: {
      main: '#1565C0', // Match navigation header active color
      light: '#1565C0',
      dark: '#1565C0',
    },
    divider: '#e5e7eb',
  },
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    // TypeScale using Major Third ratio (1.25) with 16px base
    h1: {
      fontWeight: 700,
      fontSize: '2.8rem', // ~44.8px - Reduced from 48.83px
      lineHeight: 1.1,
      letterSpacing: '-0.02em',
    },
    h2: {
      fontWeight: 600,
      fontSize: '2.2rem', // ~35.2px - Reduced from 39.06px
      lineHeight: 1.2,
      letterSpacing: '-0.01em',
    },
    h3: {
      fontWeight: 600,
      fontSize: '1.75rem', // ~28px - Reduced from 31.25px
      lineHeight: 1.3,
      letterSpacing: '-0.01em',
    },
    h4: {
      fontWeight: 600,
      fontSize: '1.4rem', // ~22.4px - Reduced from 25px
      lineHeight: 1.3,
      letterSpacing: '0',
    },
    h5: {
      fontWeight: 500,
      fontSize: '1.1rem', // ~17.6px - Reduced from 20px
      lineHeight: 1.4,
      letterSpacing: '0',
    },
    h6: {
      fontWeight: 500,
      fontSize: '0.875rem', // ~14px - Reduced from 16px
      lineHeight: 1.4,
      letterSpacing: '0',
    },
    subtitle1: {
      fontWeight: 500,
      fontSize: '1.1rem', // ~17.6px - Reduced from 20px
      lineHeight: 1.4,
      letterSpacing: '0',
    },
    subtitle2: {
      fontWeight: 500,
      fontSize: '0.875rem', // ~14px - Reduced from 16px
      lineHeight: 1.4,
      letterSpacing: '0.01em',
    },
    body1: {
      fontSize: '1rem', // 16px - Base body text
      lineHeight: 1.6,
      letterSpacing: '0',
    },
    body2: {
      fontSize: '0.8rem', // 12.8px - Secondary body text
      lineHeight: 1.5,
      letterSpacing: '0.01em',
    },
    button: {
      fontSize: '0.8rem', // 12.8px
      fontWeight: 500,
      textTransform: 'none',
      letterSpacing: '0.02em',
    },
    caption: {
      fontSize: '0.64rem', // 10.24px - Small captions
      lineHeight: 1.4,
      letterSpacing: '0.02em',
    },
    overline: {
      fontSize: '0.64rem', // 10.24px
      lineHeight: 1.4,
      letterSpacing: '0.08em',
      textTransform: 'uppercase',
    },
    // Additional TypeScale variants
    display1: {
      fontWeight: 700,
      fontSize: '3.5rem', // ~56px - Reduced from 61.04px
      lineHeight: 1.1,
      letterSpacing: '-0.02em',
    },
    display2: {
      fontWeight: 600,
      fontSize: '2.2rem', // ~35.2px - Reduced from 39.06px
      lineHeight: 1.2,
      letterSpacing: '-0.01em',
    },
    small: {
      fontSize: '0.8rem', // 12.8px
      lineHeight: 1.5,
      letterSpacing: '0.01em',
    },
    tiny: {
      fontSize: '0.64rem', // 10.24px
      lineHeight: 1.4,
      letterSpacing: '0.02em',
    },
  },
  shape: {
    borderRadius: 4,
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 600,
          borderRadius: 1,
          padding: '2px 8px',
          minHeight: '28px',
          fontSize: '0.75rem',
          boxShadow: 'none',
          border: '1px solid #e5e7eb',
          color: '#6b7280',
          backgroundColor: 'transparent',
          '&:hover': {
            backgroundColor: '#f8fafc',
            color: '#1565C0',
            borderColor: '#1565C0',
          },
        },
        contained: {
          backgroundColor: '#1565C0',
          color: '#ffffff',
          borderColor: '#1565C0',
          '&:hover': {
            backgroundColor: '#1565C0',
            borderColor: '#1565C0',
          },
        },
        outlined: {
          borderColor: '#1565C0',
          color: '#1565C0',
          '&:hover': {
            borderColor: '#1565C0',
            backgroundColor: '#f8fafc',
            color: '#1565C0',
          },
        },
        sizeSmall: {
          padding: '1px 6px',
          minHeight: '20px',
          fontSize: '0.625rem',
        },
        sizeLarge: {
          padding: '4px 12px',
          minHeight: '28px',
          fontSize: '0.875rem',
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          backgroundColor: '#ffffff',
          border: '1px solid #f1f5f9',
          borderRadius: 0, // AI Dashboard uses sharp corners
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)',
          transition: 'all 0.2s ease-in-out',
          '&:hover': {
            boxShadow: '0 4px 12px rgba(13, 71, 161, 0.08)',
            borderColor: '#1565C0',
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundColor: '#ffffff',
          backgroundImage: 'none',
          border: '1px solid #f1f5f9',
          borderRadius: 0, // AI Dashboard uses sharp corners
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)',
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 1, // AI Dashboard uses sharp corners
          fontWeight: 500,
          fontSize: '0.5rem',
          height: '20px',
          '& .MuiChip-label': {
            padding: '0 6px',
          },
        },
        sizeSmall: {
          height: '16px',
          fontSize: '0.4375rem',
          '& .MuiChip-label': {
            padding: '0 4px',
          },
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 1,
            backgroundColor: '#ffffff',
            fontSize: '0.75rem',
            '& .MuiInputBase-input': {
              padding: '8px 12px',
              fontSize: '0.75rem',
            },
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: '#000000',
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: '#000000',
              borderWidth: 2,
            },
          },
          '& .MuiInputLabel-root': {
            fontSize: '0.75rem',
            '&.Mui-focused': {
              fontSize: '0.75rem',
            },
            '&.MuiFormLabel-filled': {
              fontSize: '0.75rem',
            },
          },
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: 1, // AI Dashboard uses sharp corners
          backgroundColor: '#ffffff',
          border: '1px solid #e5e7eb',
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: '#ffffff',
          borderBottom: '1px solid #e5e7eb',
          boxShadow: '0 4px 20px rgba(13, 71, 161, 0.1)',
        },
      },
    },
    MuiToggleButton: {
      styleOverrides: {
        root: {
          color: '#6b7280',
          borderColor: '#e5e7eb',
          borderRadius: 1, // AI Dashboard uses sharp corners
          backgroundColor: 'transparent',
          '&.Mui-selected': {
            backgroundColor: '#1565C0',
            color: '#ffffff',
            borderColor: '#1565C0',
            '&:hover': {
              backgroundColor: '#1565C0',
              borderColor: '#1565C0',
            },
          },
          '&:hover': {
            backgroundColor: '#f8fafc',
            color: '#1565C0',
            borderColor: '#1565C0',
          },
        },
      },
    },
    MuiLinearProgress: {
      styleOverrides: {
        root: {
          backgroundColor: '#e5e7eb',
          borderRadius: 1,
          height: 4,
        },
        bar: {
          backgroundColor: '#1565C0',
          borderRadius: 1,
        },
      },
    },
    MuiSelect: {
      styleOverrides: {
        root: {
          fontSize: '0.75rem',
          '& .MuiSelect-select': {
            padding: '8px 12px',
            fontSize: '0.75rem',
            display: 'flex',
            alignItems: 'center',
            minHeight: '24px',
          },
          '& .MuiOutlinedInput-notchedOutline': {
            top: 0,
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            top: 0,
          },
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          fontSize: '0.75rem',
          padding: '6px 12px',
          minHeight: '32px',
          display: 'flex',
          alignItems: 'center',
        },
      },
    },
    MuiFormControl: {
      styleOverrides: {
        root: {
          '& .MuiInputLabel-root': {
            fontSize: '0.75rem',
            transform: 'translate(14px, 8px) scale(1)',
            '&.Mui-focused, &.MuiFormLabel-filled': {
              transform: 'translate(14px, -9px) scale(0.75)',
            },
          },
        },
      },
    },
    MuiInputBase: {
      styleOverrides: {
        root: {
          fontSize: '0.75rem',
          '& .MuiInputBase-input': {
            padding: '8px 12px',
            fontSize: '0.75rem',
            minHeight: '24px',
            display: 'flex',
            alignItems: 'center',
          },
        },
      },
    },
    MuiFormLabel: {
      styleOverrides: {
        root: {
          fontSize: '0.75rem',
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-notchedOutline': {
            top: 0,
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            top: 0,
          },
        },
      },
    },
    MuiCheckbox: {
      styleOverrides: {
        root: {
          padding: '4px',
          '& .MuiSvgIcon-root': {
            fontSize: '1rem',
          },
        },
      },
    },
    MuiRadio: {
      styleOverrides: {
        root: {
          padding: '4px',
          '& .MuiSvgIcon-root': {
            fontSize: '1rem',
          },
        },
      },
    },
    MuiSwitch: {
      styleOverrides: {
        root: {
          padding: '4px',
          '& .MuiSwitch-thumb': {
            width: 16,
            height: 16,
          },
          '& .MuiSwitch-track': {
            height: 12,
            borderRadius: 6,
          },
        },
      },
    },
    MuiSlider: {
      styleOverrides: {
        root: {
          height: 4,
          '& .MuiSlider-thumb': {
            width: 16,
            height: 16,
          },
        },
      },
    },
    MuiMenu: {
      styleOverrides: {
        paper: {
          borderRadius: 1,
          marginTop: '4px',
          '& .MuiMenuItem-root': {
            fontSize: '0.75rem',
            padding: '6px 12px',
            minHeight: '32px',
            display: 'flex',
            alignItems: 'center',
          },
        },
      },
    },
    MuiPopover: {
      styleOverrides: {
        paper: {
          borderRadius: 1,
          marginTop: '4px',
        },
      },
    },
  },
});

const App = () => {
  return (
    <ErrorBoundary>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <AuthProvider>
          <TaskProvider>
            <TaskSync />
            <Box
              sx={{
                minHeight: '100vh',
                background: '#f8fafc',
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <AppRoutes />
            </Box>
          </TaskProvider>
        </AuthProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
};

export default App;
